import { Field, Select, type SelectValue, Space } from '@procurify/ui'

import {
  UserSelect,
  type UserSelectOption,
} from '@webapp/components/UserSelect'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  type POBillToContactModeEnum,
  POBillToContactModes,
  type POBillToPrimaryCategoryEnum,
  POBillToPrimaryCategories,
} from '@webapp/platform/iam'
import messages from './messages'

const PRIMARY_CATEGORY_SELECT_ID = 'po-bill-to-primary-category'
const CONTACT_SELECT_ID = 'po-bill-to-contact'
const CONTACT_USER_SELECT_ID = 'po-bill-to-contact-user'

export interface IPOBillToSettingsCardProps {
  primaryCategory: POBillToPrimaryCategoryEnum
  contactMode: POBillToContactModeEnum
  selectedContactUser: Nullable<UserSelectOption>
  isContactUserRequired: boolean
  isSavingPrimaryCategory: boolean
  isSavingContact: boolean
  isLoadingContactUser: boolean
  isDisabled: boolean
  onPrimaryCategoryChange: (value: POBillToPrimaryCategoryEnum) => void
  onContactModeChange: (value: POBillToContactModeEnum) => void
  onContactUserChange: (user: Nullable<UserSelectOption>) => void
}

/**
 * Card CONTENT only (two independent selects, plus the conditional specific-
 * user picker) — the Card wrapper, heading/description, and its placement
 * are Feature Set layout.
 */
export const POBillToSettingsCard = ({
  primaryCategory,
  contactMode,
  selectedContactUser,
  isContactUserRequired,
  isSavingPrimaryCategory,
  isSavingContact,
  isLoadingContactUser,
  isDisabled,
  onPrimaryCategoryChange,
  onContactModeChange,
  onContactUserChange,
}: IPOBillToSettingsCardProps) => {
  const intl = useProcurifyIntl()

  const primaryCategoryOptions: SelectValue<POBillToPrimaryCategoryEnum>[] = [
    {
      value: POBillToPrimaryCategories.PURCHASER_HOME,
      label: intl.formatMessage(messages.purchaserHomePrimaryCategoryOption),
    },
    {
      value: POBillToPrimaryCategories.ORDER,
      label: intl.formatMessage(messages.orderPrimaryCategoryOption),
    },
    {
      value: POBillToPrimaryCategories.HEADQUARTERS,
      label: intl.formatMessage(messages.headquartersPrimaryCategoryOption),
    },
  ]

  const contactOptions: SelectValue<POBillToContactModeEnum>[] = [
    {
      value: POBillToContactModes.PURCHASER,
      label: intl.formatMessage(messages.purchaserContactOption),
    },
    {
      value: POBillToContactModes.REQUESTER,
      label: intl.formatMessage(messages.requesterContactOption),
    },
    {
      value: POBillToContactModes.SPECIFIC_USER,
      label: intl.formatMessage(messages.specificUserContactOption),
    },
    {
      value: POBillToContactModes.ACCOUNTS_PAYABLE,
      label: intl.formatMessage(messages.accountsPayableContactOption),
    },
  ]

  return (
    <Space>
      <Field
        label={intl.formatMessage(messages.primaryCategoryFieldLabel)}
        labelFor={PRIMARY_CATEGORY_SELECT_ID}
      >
        <Select<SelectValue<POBillToPrimaryCategoryEnum>>
          inputId={PRIMARY_CATEGORY_SELECT_ID}
          options={primaryCategoryOptions}
          value={primaryCategoryOptions.find(
            (option) => option.value === primaryCategory
          )}
          isDisabled={isDisabled || isSavingPrimaryCategory}
          isClearable={false}
          onChange={(option) => option && onPrimaryCategoryChange(option.value)}
        />
      </Field>

      <Field
        label={intl.formatMessage(messages.contactFieldLabel)}
        labelFor={CONTACT_SELECT_ID}
      >
        <Select<SelectValue<POBillToContactModeEnum>>
          inputId={CONTACT_SELECT_ID}
          options={contactOptions}
          value={contactOptions.find((option) => option.value === contactMode)}
          isDisabled={isDisabled || isSavingContact}
          isClearable={false}
          onChange={(option) => option && onContactModeChange(option.value)}
        />
      </Field>

      {contactMode === POBillToContactModes.SPECIFIC_USER && (
        <Field
          label={intl.formatMessage(messages.contactUserFieldLabel)}
          labelFor={CONTACT_USER_SELECT_ID}
          error={isContactUserRequired}
          helperText={
            isContactUserRequired
              ? intl.formatMessage(messages.contactUserRequiredError)
              : undefined
          }
        >
          <UserSelect<UserSelectOption>
            inputId={CONTACT_USER_SELECT_ID}
            placeholder={intl.formatMessage(
              messages.contactUserFieldPlaceholder
            )}
            value={selectedContactUser}
            isDisabled={isDisabled || isSavingContact}
            isLoading={isLoadingContactUser}
            onChange={(user) => onContactUserChange(user ?? null)}
          />
        </Field>
      )}
    </Space>
  )
}

POBillToSettingsCard.displayName = 'POBillToSettingsCard'
